import { Bot, User } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { ChatMessageData } from './ChatWidget.types';

interface ChatMessageProps {
  message: ChatMessageData;
  locale?: string;
}

/**
 * Componente que renderiza un mensaje individual del chat.
 *
 * Los mensajes del asistente se renderizan con soporte completo de Markdown
 * (negrita, cursiva, código inline, bloques de código, listas, headers, links, etc.).
 * Los mensajes del usuario se muestran como texto plano.
 *
 * @see docs/colors.md - Sistema de colores
 */
export function ChatMessage({ message, locale = 'en' }: ChatMessageProps) {
  const isUser = message.role === 'user';

  const formattedTime = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(message.timestamp);

  return (
    <div
      className={`flex gap-2 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}
      data-testid={`chat-message-${message.role}`}
    >
      {/* Avatar */}
      <div
        className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${
          isUser
            ? 'bg-blue-100 dark:bg-blue-900/30'
            : 'bg-gray-100 dark:bg-gray-700'
        }`}
      >
        {isUser ? (
          <User className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        ) : (
          <Bot className="h-4 w-4 text-gray-600 dark:text-gray-400" />
        )}
      </div>

      {/* Burbuja de mensaje */}
      <div className={`max-w-[75%] ${isUser ? 'text-right' : 'text-left'}`}>
        <div
          className={`inline-block rounded-2xl px-4 py-2 text-sm ${
            isUser
              ? 'rounded-br-md bg-blue-600 text-white dark:bg-blue-500'
              : 'rounded-bl-md bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-100'
          }`}
        >
          {isUser ? (
            // Mensajes del usuario: texto plano
            <span>{message.content}</span>
          ) : (
            // Mensajes del asistente: Markdown completo
            <div className="chat-markdown">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  // Párrafos
                  p: ({ children }) => (
                    <p className="mb-2 last:mb-0 leading-relaxed">{children}</p>
                  ),
                  // Headers
                  h1: ({ children }) => (
                    <h1 className="mb-2 mt-3 text-base font-bold first:mt-0">{children}</h1>
                  ),
                  h2: ({ children }) => (
                    <h2 className="mb-2 mt-3 text-sm font-bold first:mt-0">{children}</h2>
                  ),
                  h3: ({ children }) => (
                    <h3 className="mb-1 mt-2 text-sm font-semibold first:mt-0">{children}</h3>
                  ),
                  h4: ({ children }) => (
                    <h4 className="mb-1 mt-2 text-sm font-semibold first:mt-0">{children}</h4>
                  ),
                  // Énfasis
                  strong: ({ children }) => (
                    <strong className="font-semibold">{children}</strong>
                  ),
                  em: ({ children }) => (
                    <em className="italic">{children}</em>
                  ),
                  // Código inline
                  code: ({ children, className }) => {
                    const isBlock = className?.includes('language-');
                    if (isBlock) {
                      return (
                        <code className={className}>{children}</code>
                      );
                    }
                    return (
                      <code className="rounded bg-gray-200 px-1 py-0.5 font-mono text-xs dark:bg-gray-600 dark:text-gray-100">
                        {children}
                      </code>
                    );
                  },
                  // Bloque de código
                  pre: ({ children }) => (
                    <pre className="my-2 overflow-x-auto rounded-lg bg-gray-800 p-3 font-mono text-xs text-gray-100 dark:bg-gray-900 dark:text-gray-100">
                      {children}
                    </pre>
                  ),
                  // Listas
                  ul: ({ children }) => (
                    <ul className="mb-2 ml-4 list-disc space-y-0.5 last:mb-0">{children}</ul>
                  ),
                  ol: ({ children }) => (
                    <ol className="mb-2 ml-4 list-decimal space-y-0.5 last:mb-0">{children}</ol>
                  ),
                  li: ({ children }) => (
                    <li className="leading-relaxed">{children}</li>
                  ),
                  // Cita
                  blockquote: ({ children }) => (
                    <blockquote className="my-2 border-l-2 border-gray-400 pl-3 italic text-gray-600 dark:border-gray-500 dark:text-gray-300">
                      {children}
                    </blockquote>
                  ),
                  // Separador
                  hr: () => (
                    <hr className="my-2 border-gray-300 dark:border-gray-600" />
                  ),
                  // Links
                  a: ({ href, children }) => (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline decoration-blue-500 underline-offset-2 hover:text-blue-600 dark:hover:text-blue-300"
                    >
                      {children}
                    </a>
                  ),
                  // Tabla (GFM)
                  table: ({ children }) => (
                    <div className="my-2 overflow-x-auto">
                      <table className="min-w-full border-collapse text-xs">
                        {children}
                      </table>
                    </div>
                  ),
                  thead: ({ children }) => (
                    <thead className="border-b border-gray-300 dark:border-gray-600">
                      {children}
                    </thead>
                  ),
                  tbody: ({ children }) => <tbody>{children}</tbody>,
                  tr: ({ children }) => (
                    <tr className="border-b border-gray-200 dark:border-gray-700">{children}</tr>
                  ),
                  th: ({ children }) => (
                    <th className="px-2 py-1 text-left font-semibold">{children}</th>
                  ),
                  td: ({ children }) => (
                    <td className="px-2 py-1">{children}</td>
                  ),
                }}
              >
                {message.content}
              </ReactMarkdown>
            </div>
          )}
        </div>
        <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
          {formattedTime}
        </p>
      </div>
    </div>
  );
}
